import React, { useState } from 'react';
import { Code, Brain, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const SkillsSection = ({ skills, cvService }) => {
  const [expandedCategories, setExpandedCategories] = useState(new Set(['hard']));

  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const renderSkillLevel = (examples) => {
    if (!examples || examples.length === 0) return null;
    
    return (
      <div className="mt-2">
        <div className="flex flex-wrap gap-2">
          {examples.map((example, index) => {
            const exampleText = cvService.getTranslatedValue(example);
            return (
              <Badge
                key={index}
                variant="outline"
                className="text-xs"
              >
                {exampleText}
              </Badge>
            );
          })}
        </div>
      </div>
    );
  };

  if (!skills || skills.length === 0) return null;

  const hardSkills = skills.filter(skill => skill.type === 'hard');
  const softSkills = skills.filter(skill => skill.type === 'soft');

  const skillCategories = [
    {
      id: 'hard',
      title: cvService.locale === 'fr' ? 'Compétences Techniques' : 'Technical Skills',
      icon: <Code size={20} />,
      skills: hardSkills,
      color: 'blue'
    },
    {
      id: 'soft',
      title: cvService.locale === 'fr' ? 'Compétences Transversales' : 'Soft Skills',
      icon: <Brain size={20} />,
      skills: softSkills,
      color: 'green'
    }
  ];

  return (
    <div className="mb-2">
      {/* Contenu principal */}
      <div className=" backdrop-blur-md rounded-xl p-4 border border-hair shadow-xl">
        <div className="flex items-center mb-8">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-space-600 to-space-700 flex items-center justify-center mr-4 shadow-lg">
            <Star className="text-white" size={20} />
          </div>
          <h2 className="text-2xl font-bold text-ink tracking-tight">
            {cvService.locale === 'fr' ? 'Compétences' : 'Skills'}
          </h2>
        </div>
        
        <div className="space-y-6">
        {skillCategories.map((category) => {
          if (category.skills.length === 0) return null;
          
          const isExpanded = expandedCategories.has(category.id);
          const colorVariants = {
            blue: {
              headerBg: 'bg-cyan/10 dark:bg-blue-950',
              icon: 'text-cyan dark:text-blue-400',
              text: 'text-cyan-soft dark:text-blue-200',
              hover: 'hover:bg-cyan/15 dark:hover:bg-cyan/25'
            },
            green: {
              headerBg: 'bg-green-500/10 dark:bg-green-950',
              icon: 'text-green-400 dark:text-green-400',
              text: 'text-green-300 dark:text-green-200',
              hover: 'hover:bg-green-500/15 dark:hover:bg-green-900'
            }
          };
          
          const colors = colorVariants[category.color];

          return (
            <Card key={category.id} className="bg-gradient-to-br from-space-800 to-space-900/50 backdrop-blur-sm border-hair shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl overflow-hidden">
              <CardHeader 
                className="cursor-pointer bg-gradient-to-r from-space-800 to-space-900 hover:from-space-700 hover:to-space-900 transition-all duration-300 border-b border-hair"
                onClick={() => toggleCategory(category.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-space-600 to-space-700 flex items-center justify-center shadow-lg">
                      <div className="text-white">
                        {category.icon}
                      </div>
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-ink tracking-tight">
                        {category.title}
                      </CardTitle>
                      <Badge variant="secondary" className="mt-1 text-xs bg-space-700 text-ink-muted border-hair">
                        {category.skills.length} {cvService.locale === 'fr' ? 'compétences' : 'skills'}
                      </Badge>
                    </div>
                  </div>
                  
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-ink-dim hover:text-ink-muted hover:bg-space-700 transition-colors">
                    {isExpanded ? (
                      <ChevronUp size={18} />
                    ) : (
                      <ChevronDown size={18} />
                    )}
                  </Button>
                </div>
              </CardHeader>

              {/* Content */}
              {isExpanded && (
                <CardContent className="p-6 bg-gradient-to-br from-space-800 to-space-900/30">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {category.skills.map((skill) => {
                      const label = cvService.getTranslatedValue(skill.label);
                      const description = cvService.getTranslatedValue(skill.description);

                      return (
                        <Card
                          key={skill.id}
                          className="bg-space-800/70 backdrop-blur-sm border-hair hover:bg-space-700 hover:border-hair-bright hover:shadow-md transition-all duration-300 rounded-lg"
                        >
                          <CardContent className="p-4">
                            <h4 className="font-semibold text-ink mb-2 text-sm flex items-center">
                              <div className="w-2 h-2 bg-space-600 rounded-full mr-2"></div>
                              {label}
                            </h4>
                            
                            {description && (
                              <p className="text-ink-muted text-xs mb-3 leading-relaxed font-medium">
                                {description}
                              </p>
                            )}
                            
                            {/* Examples */}
                            {renderSkillLevel(skill.examples)}
                            
                            {/* Q&A indicator */}
                            {skill.qa && skill.qa.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-hair">
                                <Badge variant="outline" className="text-xs bg-space-800 text-ink-muted border-hair">
                                  {skill.qa.length} {cvService.locale === 'fr' ? 'question(s)' : 'question(s)'}
                                </Badge>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
        </div>
      </div>
    </div>
  );
};

export default SkillsSection;
